import React, { useState, useEffect } from "react";
import api from "../../api/axiosInstance";
import { useNavigate } from "react-router-dom";

import NavigationTabBar from "../../components/navigation/NavigationTabBar";
import { useAuth } from "../../components/navigation/AuthenticationStateManager";
import FilterToolbar from "./components/FilterToolbar";
import FeaturedSection from "./components/FeaturedSection";
import BlogGrid from "./components/BlogGrid";
import PaginationControls from "./components/PaginationControls";
import Icon from "../../components/AppIcon";
import FooterSection from "../../components/navigation/FooterSection";

const POSTS_PER_PAGE = 9;

const BlogContentHub = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  /* ================= FETCH ONLY PUBLISHED BLOGS ================= */
  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setLoading(true);
        const res = await api.get("/api/blogs");
        const published = (res.data?.data || []).filter(
          (b) => b.isPublished
        );
        setBlogs(published);
      } catch (err) {
        console.error("Failed to load blogs", err);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  /* ================= FILTERING ================= */
  const categories = [
    "all",
    ...new Set(
      blogs
        .map((b) => b.category?.name)
        .filter(Boolean)
    ),
  ];

  const filterByDate = (posts) => {
    if (dateRange === "all") return posts;

    const now = new Date();
    const cutoff = new Date();

    if (dateRange === "week") cutoff.setDate(now.getDate() - 7);
    if (dateRange === "month") cutoff.setMonth(now.getMonth() - 1);
    if (dateRange === "year") cutoff.setFullYear(now.getFullYear() - 1);

    return posts.filter(
      (p) => p.publishedAt && new Date(p.publishedAt) >= cutoff
    );
  };

  const filteredBlogs = filterByDate(
    blogs.filter((post) => {
      const matchesSearch =
        !searchQuery ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        selectedCategory === "all" ||
        post.category?.name === selectedCategory;

      return matchesSearch && matchesCategory;
    })
  );

  const featuredBlogs = filteredBlogs.filter((b) => b.isFeatured);
  const normalBlogs = filteredBlogs.filter((b) => !b.isFeatured);

  const totalPages = Math.ceil(normalBlogs.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const paginatedBlogs = normalBlogs.slice(
    startIndex,
    startIndex + POSTS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, dateRange]);

  /* ================= SLUG NAVIGATION ================= */
  const handleReadMore = (slug) => {
    navigate(`/blog/${slug}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationTabBar isAuthenticated={isAuthenticated} />

      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="flex justify-center items-center gap-3 mb-4">
              <Icon name="BookOpen" size={40} />
              <h1 className="text-4xl font-heading font-bold">
                Blog Content Hub
              </h1>
            </div>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Insights, interviews, and career guidance.
            </p>
          </div>

          <FilterToolbar
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            totalPosts={blogs.length}
            filteredPosts={filteredBlogs.length}
          />

          {featuredBlogs.length > 0 && (
            <FeaturedSection
              featuredPosts={featuredBlogs}
              onReadMore={handleReadMore}
            />
          )}

          <BlogGrid
            posts={paginatedBlogs}
            onReadMore={handleReadMore}
            loading={loading}
          />

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            postsPerPage={POSTS_PER_PAGE}
            totalPosts={normalBlogs.length}
          />
        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default BlogContentHub;
